import { fetchAddendum } from '@/api/addendum.api';
import { fetchTaskRelations } from '@/api/basecamp.api';
import { getMFHProcess } from '@/api/mfh';
import { getMFHPortfolioProcess } from '@/api/mfh/mfh-portfolio-process.api';
import { useRouteParams } from '@/hooks/util.hooks';
import { CCSRouteMap, MFHPortfolioProcessType, MFHProcessType, resolveRoutePath } from '@condo/domain';
import type { RouteRecordRaw } from 'vue-router';

export const notificationRoute: RouteRecordRaw = {
    ...CCSRouteMap['notification'],
    component: () => null,
    async beforeEnter(to, _from, next) {
        const { getRouteParam } = useRouteParams(to);
        const t = getRouteParam('t', String);

        switch (t) {
            case 'note-mfh-sourcing-process': {
                const mfhSourcingProcessId = getRouteParam('mfhSourcingProcessId', String);
                if (mfhSourcingProcessId) {
                    const process = await getMFHProcess({
                        processType: MFHProcessType.Sourcing,
                        processId: +mfhSourcingProcessId,
                    });

                    next(
                        resolveRoutePath(CCSRouteMap['one.mfh.sourcing.overview'], {
                            params: { processId: mfhSourcingProcessId.toString(), mfhId: process.mfhId.toString() },
                        }),
                    );
                    return;
                }
                break;
            }
            case 'note-mfh-subsidy-financing-process': {
                const mfhSubsidyFinancingProcessId = getRouteParam('mfhSubsidyFinancingProcessId', String);
                if (mfhSubsidyFinancingProcessId) {
                    const process = await getMFHProcess({
                        processType: MFHProcessType.SubsidyFinancing,
                        processId: +mfhSubsidyFinancingProcessId,
                    });

                    next(
                        resolveRoutePath(CCSRouteMap['one.mfh.subsidy-financing.overview'], {
                            params: { processId: mfhSubsidyFinancingProcessId.toString(), mfhId: process.mfhId.toString() },
                        }),
                    );
                    return;
                }
                break;
            }
            case 'note-mfh-estate-management-process': {
                const mfhEstateManagementProcessId = getRouteParam('mfhEstateManagementProcessId', String);
                if (mfhEstateManagementProcessId) {
                    const process = await getMFHProcess({
                        processType: MFHProcessType.EstateManagement,
                        processId: +mfhEstateManagementProcessId,
                    });

                    next(
                        resolveRoutePath(CCSRouteMap['one.mfh.estate-management.overview'], {
                            params: { processId: mfhEstateManagementProcessId.toString(), mfhId: process.mfhId.toString() },
                        }),
                    );
                    return;
                }
                break;
            }
            case 'note-mfh-portfolio-financing-process': {
                const mfhPortfolioFinancingProcessId = getRouteParam('mfhPortfolioFinancingProcessId', String);
                if (mfhPortfolioFinancingProcessId) {
                    const process = await getMFHPortfolioProcess({
                        processType: MFHPortfolioProcessType.Financing,
                        processId: +mfhPortfolioFinancingProcessId,
                    });

                    next(
                        resolveRoutePath(CCSRouteMap['one.mfh-portfolio.financing.overview'], {
                            params: { processId: mfhPortfolioFinancingProcessId.toString(), mfhPortfolioId: process.mfhPortfolioId.toString() },
                        }),
                    );
                    return;
                }

                break;
            }
            case 'note-mfh-portfolio-purchasing-process': {
                const mfhPortfolioPurchasingProcessId = getRouteParam('mfhPortfolioPurchasingProcessId', String);
                if (mfhPortfolioPurchasingProcessId) {
                    const process = await getMFHPortfolioProcess({
                        processType: MFHPortfolioProcessType.Purchasing,
                        processId: +mfhPortfolioPurchasingProcessId,
                    });

                    next(
                        resolveRoutePath(CCSRouteMap['one.mfh-portfolio.purchasing.overview'], {
                            params: { processId: mfhPortfolioPurchasingProcessId.toString(), mfhPortfolioId: process.mfhPortfolioId.toString() },
                        }),
                    );
                    return;
                }
                break;
            }
            case 'task': {
                const taskId = getRouteParam('taskId', Number);
                if (taskId) {
                    const task = await fetchTaskRelations(+taskId);
                    if (task.estateId) {
                        next(
                            resolveRoutePath(CCSRouteMap['to-do'], {
                                params: { estateId: task.estateId.toString() },
                                query: {
                                    taskId: taskId,
                                },
                            }),
                        );
                        return;
                    }
                    if (task.mfhId) {
                        next(
                            resolveRoutePath(CCSRouteMap['one.mfh.tasks'], {
                                params: { mfhId: task.mfhId.toString() },
                                query: {
                                    taskId: taskId,
                                },
                            }),
                        );
                        return;
                    }
                    if (task.mfhPortfolioId) {
                        next(
                            resolveRoutePath(CCSRouteMap['one.mfh-portfolio.tasks'], {
                                params: { mfhPortfolioId: task.mfhPortfolioId.toString() },
                                query: {
                                    taskId: taskId,
                                },
                            }),
                        );
                        return;
                    }
                    if (task.mfhRenovationProjectId) {
                        const mfhId = task.mfhRenovationProject?.mfhId;
                        if (mfhId) {
                            next(
                                resolveRoutePath(CCSRouteMap['one.mfh.tasks'], {
                                    params: { mfhId: mfhId.toString() },
                                    query: {
                                        taskId: taskId,
                                    },
                                }),
                            );
                            return;
                        }
                    }
                    if (task.mfhRenovationProjectPlanningModuleId) {
                        const mfhId = task.mfhRenovationProjectPlanningModule?.mfhRenovationProject?.mfhId;
                        if (mfhId) {
                            next(
                                resolveRoutePath(CCSRouteMap['one.mfh.tasks'], {
                                    params: { mfhId: mfhId.toString() },
                                    query: {
                                        taskId: taskId,
                                    },
                                }),
                            );
                        }
                    }
                    if (task.mfhRenovationProjectLotId) {
                        const mfhId = task.mfhRenovationProjectLot?.mfhRenovationProject?.mfhId;
                        if (mfhId) {
                            next(
                                resolveRoutePath(CCSRouteMap['one.mfh.tasks'], {
                                    params: { mfhId: mfhId.toString() },
                                    query: {
                                        taskId: taskId,
                                    },
                                }),
                            );
                        }
                    }
                }
                break;
            }
            case 'addendum': {
                const addendumId = getRouteParam('addendumId', Number);
                if (addendumId) {
                    const addednum = await fetchAddendum(addendumId, {
                        select: ['addendumId', 'mfhId'],
                    });
                    next(
                        resolveRoutePath(CCSRouteMap['one.mfh.addenda'], {
                            params: { mfhId: addednum.mfhId.toString() },
                            query: { addendumId: addendumId.toString() },
                        }),
                    );
                    return;
                }
                break;
            }
            default:
                next('/not-found');
        }
    },
};
